import {css, html, LitElement} from 'lit';
import type {PropertyValues} from 'lit';
import {copySvg, crossSvg, linkSvg} from '../../helpers/elements.ts';
import {state, customElement} from 'lit/decorators.js';
import {msg} from '@lit/localize';
import {showCoordinates$} from '../../stores/store-map.ts';
import type {Subscription} from 'rxjs';
import {panPopupIntoView} from '../../map/creation.ts';

@customElement('element-coordinates')
export default class ElementCoordinates extends LitElement {
  @state() coordinates: string | undefined = undefined;
  @state() coords: number[] | undefined = undefined;
  private showCoordinates: Subscription;
  private integerFormat = new Intl.NumberFormat('de-CH', {
    maximumFractionDigits: 0,
  });

  static override styles = css`
    .coordinates-content {
      font-family: 'Inter Variable', sans-serif;
      letter-spacing: 0.14px;
      width: 180px;
      background-color: white;
      padding: 10px 12px;
      font-size: 14px;
      line-height: 22px;
      font-weight: normal;
      box-shadow: 0 2px 4px #49505766;
    }

    .coordinates-content::after,
    .coordinates-content::before {
      top: 100%;
      border: solid transparent;
      content: ' ';
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
    }

    .coordinates-content::after {
      border-top-color: white;
      border-top-width: 11px;
      border-right-width: 6px;
      border-bottom-width: 0;
      border-left-width: 6px;
      left: 30px;
      margin-left: -6px;
    }

    .coordinates-content:hover {
      fill: #ad091d;
    }

    .close-btn-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .close-btn-container > div {
      margin-bottom: auto;
      cursor: pointer;
    }

    .coordinates-content > button > svg,
    .close-btn-container > div svg {
      height: 24px;
      width: 24px;
    }

    .coordinates-content > button svg,
    .close-btn-container > div svg {
      fill: #d4021c;
    }

    .coordinates-content > button:hover,
    .coordinates-content > button:hover svg,
    .close-btn-container > div > svg:hover {
      fill: #ad091d;
      color: #ad091d;
    }

    .coordinates-content > button {
      margin-top: 10px;
      display: flex;
      align-items: center;
      cursor: pointer;
      -webkit-appearance: button;
      background-color: transparent;
      background-image: none;
      border: none;
    }

    .coordinates-content > button > div {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .coordinates-content > button span {
      padding-left: 8px;
      padding-top: 2.25px;
      text-align: left;
      font-weight: bold;
      font-size: 14px;
      font-family: 'Inter Variable', sans-serif;
      letter-spacing: 0.14px;
    }
  `;

  async copy() {
    await navigator.clipboard.writeText(this.coordinates);
    if (this.coordinates !== undefined) {
      this.coordinates = undefined;
      this.coords = undefined;
      this.dispatchEvent(
        new CustomEvent('alertnotif', {
          bubbles: true,
          detail: {
            type: 'success',
            message: msg('The coordinates have been copied to the clipboard.'),
          },
        }),
      );
    }
  }

  async copyPosition() {
    if (this.coordinates !== undefined) {
      this.coordinates = undefined;
      this.dispatchEvent(
        new CustomEvent('copyPosition', {
          bubbles: true,
          detail: {
            coordinates: this.coords,
          },
        }),
      );
      this.coords = undefined;
    }
  }

  override connectedCallback() {
    this.showCoordinates = showCoordinates$.subscribe((coords) => {
      this.coords = !coords ? undefined : coords;
      this.coordinates = !coords
        ? undefined
        : `${this.integerFormat.format(coords[0])}, ${this.integerFormat.format(
            coords[1],
          )}`;
    });
    super.connectedCallback();
  }

  override disconnectedCallback() {
    this.showCoordinates.unsubscribe();
    super.disconnectedCallback();
  }

  override updated(_changedProperties: PropertyValues) {
    panPopupIntoView('coordinates');
    super.updated(_changedProperties);
  }

  override render() {
    this.style.zIndex = '50';
    this.style.position = 'absolute';
    this.style.transform = 'translate(-30px, calc(-100% - 12px))';
    const copyCoordsMessage = msg('Copy coordinates', {
      desc: 'button to copy coordinates with right-click on map',
    });
    const copyPositionMessage = msg('Copy position', {
      desc: 'button to copy position with right-click on map',
    });
    return html`<div
      .hidden="${this.coordinates === undefined}"
      class="coordinates-content"
    >
      <div class="close-btn-container">
        <span
          >${msg('Position on the map', {
            desc: 'right-click on map overlay',
          })}</span
        >
        <div
          @click="${() => {
            this.coords = undefined;
            this.coordinates = undefined;
          }}"
        >
          ${crossSvg()}
        </div>
      </div>
      <button @click=${() => this.copy()}>
        ${copySvg('', copyCoordsMessage)}
        <span>${copyCoordsMessage}</span>
      </button>
      <button @click=${() => this.copyPosition()}>
        <div>${linkSvg('', copyPositionMessage)}</div>
        <span>${copyPositionMessage}</span>
      </button>
    </div>`;
  }
}
